/**
 * Where a magic link lands. Exchanges the token in the URL for a session,
 * then sends the member straight to whatever it was for — signing in, or a
 * specific ask waiting on the account page.
 */

import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'wouter-preact';
import type { JSX } from 'preact';

import { apiPost, setSession, ApiError } from '../api.js';

/**
 * A dead link with nobody to call. If redeem fails, the token is still good
 * for one thing: identifying whose row to attach a note to, so a message
 * typed here reaches the farm without needing a working session — the exact
 * thing that just failed.
 */
function HelpForm({ token }: { token: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  async function send(event: Event): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setSendError(null);
    try {
      await apiPost('/public/v1/member/help', { token, message });
      setSent(true);
    } catch (err) {
      setSendError(
        err instanceof ApiError && err.status === 404
          ? "We couldn't match that to an account — email hello@flockandfurrow.farm instead."
          : 'Could not send that. Try again in a moment.',
      );
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return <p class="notice" role="status">Sent — the farm will reach back out.</p>;
  }

  if (!open) {
    return (
      <button class="link" onClick={() => setOpen(true)}>
        Need help?
      </button>
    );
  }

  return (
    <form class="compose" onSubmit={(event) => void send(event)}>
      <label>
        What's going on?
        <textarea
          value={message}
          onInput={(event) => setMessage((event.target as HTMLTextAreaElement).value)}
          required
        />
      </label>
      {sendError && <p class="error" role="alert">{sendError}</p>}
      <button class="primary" type="submit" disabled={busy || !message.trim()}>
        {busy ? 'Sending…' : 'Send'}
      </button>
    </form>
  );
}

export function RedeemView(): JSX.Element {
  const [, navigate] = useLocation();
  const [error, setError] = useState<string | null>(null);
  const token = new URLSearchParams(window.location.search).get('token');

  useEffect(() => {
    if (!token) {
      setError('That link is missing its token — copy the whole link from the email.');
      return;
    }

    apiPost<{ session_token: string; redirect_hint: string }>('/public/v1/member/redeem', { token })
      .then((result) => {
        setSession(result.session_token);
        navigate(`/${result.redirect_hint}`, { replace: true });
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not sign you in. Try the link again.');
      });
  }, []);

  return (
    <main class="view centred">
      {error ? (
        <>
          <h1>That link didn't work</h1>
          <p class="muted">{error}</p>
          <p class="muted small">If it's expired or already used, ask the farm to send you a new one.</p>
          {token && <HelpForm token={token} />}
        </>
      ) : (
        <p class="muted">Signing you in…</p>
      )}
    </main>
  );
}
